import { CommandGroup, CommandItem, CommandPalette } from 'components/CommandPalette'
import { useTheme } from 'hooks'
import {
  CalendarDays,
  CreditCard,
  FolderOpen,
  Home,
  KeyRound,
  LogOut,
  MessageCircleHeart,
  Moon,
  Palette,
  Plug,
  Sparkles,
  Sun,
  User,
  Users,
} from 'lucide-react'
import { logoutAction } from 'modules/authentication/slices'
import { useAppDispatch } from 'modules/core'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { intercom } from 'services/intercom/intercom.service'
import { dispatchOpenAssistantChat } from '../assistant-chat-events'
import { dockSurfaceForPath } from '../surfaces'
import { useAssistantChatUIStore } from '../store/assistant-chat-ui-store'

/**
 * Global cmd-k command palette. Owns the keybind, the open state, and the
 * static command registry (navigate, theme, support, log out). Mounted once
 * in AuthAppLayout. Spec: playbook/design-system/components.md#command-palette.
 */
export function AppCommandPalette() {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { theme, toggleTheme } = useTheme()

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen(prev => !prev)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const go = (path: string) => () => {
    navigate(path)
    setOpen(false)
  }

  const handleToggleTheme = () => {
    toggleTheme()
    setOpen(false)
  }

  const handleSupport = () => {
    intercom.show()
    setOpen(false)
  }

  const handleAskTaka = () => {
    setOpen(false)
    // Open via the STORE, not only the event: the event races component mount
    // when we navigate (listener attaches after the route renders), while the
    // persisted store flag is read on mount — the panel opens either way.
    useAssistantChatUIStore.getState().setOpen(true)
    dispatchOpenAssistantChat('cmdk')
    // Rooms with a resident agent (composer/brand) or unmapped screens have no
    // dock — take the user home, where the assistant panel opens from the flag.
    if (location.pathname !== '/' && !dockSurfaceForPath(location.pathname)) {
      navigate('/')
    }
  }

  const handleLogout = () => {
    setOpen(false)
    dispatch(logoutAction())
    navigate('/login')
  }

  const NextThemeIcon = theme === 'dark' ? Sun : Moon
  const nextTheme = theme === 'dark' ? 'light' : 'dark'

  return (
    <CommandPalette open={open} onOpenChange={setOpen}>
      <CommandGroup heading="Navigate">
        <CommandItem onSelect={go('/')} icon={<Home size={14} />} shortcut="g h">
          Home
        </CommandItem>
        <CommandItem onSelect={go('/calendar')} icon={<CalendarDays size={14} />} shortcut="g c">
          Calendar
        </CommandItem>
        <CommandItem onSelect={go('/posts')} icon={<FolderOpen size={14} />} shortcut="g p">
          Posts
        </CommandItem>
        <CommandItem onSelect={go('/brand')} icon={<Palette size={14} />}>
          Brand
        </CommandItem>
        <CommandItem onSelect={go('/integrations')} icon={<Plug size={14} />}>
          Integrations
        </CommandItem>
        <CommandItem onSelect={go('/collaborators')} icon={<Users size={14} />}>
          Collaborators
        </CommandItem>
        <CommandItem onSelect={go('/billing')} icon={<CreditCard size={14} />}>
          Billing
        </CommandItem>
        <CommandItem
          onSelect={go('/api-keys')}
          icon={<KeyRound size={14} />}
          value="api keys mcp developers integrations token"
        >
          API & MCP
        </CommandItem>
        <CommandItem onSelect={go('/profile')} icon={<User size={14} />}>
          Profile
        </CommandItem>
      </CommandGroup>

      <CommandGroup heading="AI Agent">
        <CommandItem
          onSelect={handleAskTaka}
          icon={<Sparkles size={14} />}
          value="ai agent taka ask chat talk draft brief"
        >
          Open AI Agent
        </CommandItem>
      </CommandGroup>

      <CommandGroup heading="Settings">
        <CommandItem onSelect={handleToggleTheme} icon={<NextThemeIcon size={14} />}>
          Switch to {nextTheme}
        </CommandItem>
        <CommandItem onSelect={handleSupport} icon={<MessageCircleHeart size={14} />}>
          Get support
        </CommandItem>
        <CommandItem onSelect={handleLogout} icon={<LogOut size={14} />}>
          Log out
        </CommandItem>
      </CommandGroup>
    </CommandPalette>
  )
}
